/**
 * URL-bound manual-book filter (`book`) — ADR-0042 · #84.
 *
 * Not `useSearchEnumParam`: that hook always WRITES a value, and the off state
 * here is the absence of the key. Writing `book=0` would put a token in every
 * shared link for the default state, which is exactly what `exp` and `range`
 * avoid — and it would make the whole-universe view a pinned choice rather
 * than the default that in-place navigation preserves.
 */
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback } from 'react';

import { BOOK_FILTER_ON, type BookFilterParam } from '@meridian/contracts';

export function useBookParam(): {
  readonly book: BookFilterParam | undefined;
  readonly setBook: (next: BookFilterParam | undefined) => void;
} {
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const raw = (search as Record<string, unknown>)['book'];
  /* Accept the string form too: a hand-typed `?book=1` arrives unparsed on the
     loose cross-route read, the same reason the schema preprocesses it. */
  const book = raw === BOOK_FILTER_ON || raw === '1' ? BOOK_FILTER_ON : undefined;

  const setBook = useCallback(
    (next: BookFilterParam | undefined) => {
      void navigate({
        // @ts-expect-error — cross-route search patch; Zod revalidates on read
        search: (prev: Record<string, unknown>) => {
          const { book: _dropped, ...rest } = prev;
          return next === undefined ? rest : { ...rest, book: next };
        },
        replace: true,
      });
    },
    [navigate],
  );

  return { book, setBook };
}
